import { Box, Button, TextField, Typography, Alert } from '@mui/material'
import React, { useState } from 'react'
import { ActionCodeSettings } from 'firebase/auth'

// Define prop types
type EnterEmailProps = {
  setEmail: (email: string) => void
  sendPasswordResetEmail: (
    email: string,
    actionCodeSettings?: ActionCodeSettings
  ) => Promise<boolean | void>
  setEmailSent: (sent: boolean) => void
  email: string
  sending?: boolean
  hookError?: Error
}

const EnterEmail: React.FC<EnterEmailProps> = ({
  setEmail,
  sendPasswordResetEmail,
  setEmailSent,
  email,
  sending = false,
  hookError,
}) => {
  const [localError, setLocalError] = useState<string | null>(null)

  const handleSendResetEmail = async () => {
    const trimmedEmail = email.trim()
    if (!trimmedEmail) {
      setLocalError('Please enter your email address')
      return
    }

    setLocalError(null)

    try {
      const actionCodeSettings: ActionCodeSettings = {
        url: `${window.location.origin}/set-password`,
        handleCodeInApp: true,
      }
      await sendPasswordResetEmail(trimmedEmail, actionCodeSettings)
      setEmailSent(true)
    } catch (err) {
      // Error will be captured by hookError from the parent
      // But also handle locally in case hook doesn't capture it
      const errorMessage =
        err instanceof Error ? err.message : 'An error occurred. Please try again.'
      setLocalError(errorMessage)
    }
  }

  const displayError = localError || (hookError ? hookError.message : null)

  return (
    <>
      <Box></Box>
      <Box>
        <Typography sx={{ alignSelf: 'center', fontSize: '22px', fontWeight: 500 }}>
          Forgotten password
        </Typography>
        <Typography style={{ color: 'gray', fontWeight: 400, fontSize: '18px' }}>
          Please enter your email address and we'll send you a link to reset it.
        </Typography>
      </Box>

      {displayError && (
        <Box>
          <Alert severity="error">{displayError}</Alert>
        </Box>
      )}

      <Box>
        <TextField
          id="emailTextField"
          label="Email"
          variant="outlined"
          sx={{ width: '100%' }}
          onChange={(e) => {
            setEmail(e.target.value)
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              handleSendResetEmail()
            }
          }}
        />
      </Box>

      <Box>
        <Button
          sx={{ width: '100%' }}
          variant="contained"
          disabled={sending}
          onClick={handleSendResetEmail}
        >
          {sending ? 'Sending...' : 'Send email'}
        </Button>
      </Box>
    </>
  )
}

export default EnterEmail
